import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { CalendarBlank, MapPin, Clock, Plus, Minus, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EventDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [qty, setQty] = useState({});

  useEffect(() => {
    api.get(`/events/${id}`).then(({ data }) => {
      setEvent(data);
      const init = {};
      data.ticket_types.forEach((t) => (init[t.id] = 0));
      setQty(init);
    });
  }, [id]);

  if (!event) return <div className="p-20 text-center text-neutral-500" data-testid="event-loading">Loading event…</div>;

  const total = event.ticket_types.reduce((sum, t) => sum + (qty[t.id] || 0) * t.price, 0);
  const totalCount = Object.values(qty).reduce((a, b) => a + b, 0);
  const date = new Date(event.date);

  const onCheckout = () => {
    if (totalCount === 0) {
      toast.error("Select at least 1 ticket");
      return;
    }
    if (!user) {
      sessionStorage.setItem("pending_checkout", JSON.stringify({ event_id: event.id, qty }));
      nav("/login?next=" + encodeURIComponent(`/checkout/${event.id}`));
      return;
    }
    sessionStorage.setItem("pending_checkout", JSON.stringify({ event_id: event.id, qty }));
    nav(`/checkout/${event.id}`);
  };

  return (
    <div data-testid="event-detail-page">
      <section className="relative bg-[#0A0A0A] overflow-hidden">
        <div
          className="absolute inset-0 bg-center bg-cover blur-2xl opacity-40 scale-110"
          style={{ backgroundImage: `url(${event.image_url})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center text-white">
          <div className="md:col-span-5 lg:col-span-4">
            <div className="aspect-square w-full max-w-md mx-auto md:mx-0 overflow-hidden border border-white/10 bg-black/40 rounded-2xl shadow-xl">
              <img src={event.image_url} alt={event.title} className="w-full h-full object-contain" />
            </div>
          </div>
          <div className="md:col-span-7 lg:col-span-8">
            <span className="label-eyebrow text-white/80">{event.category}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl mt-2 tracking-tighter leading-[0.95] break-words" data-testid="event-title">{event.title}</h1>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm">
              <span className="flex items-center gap-2"><CalendarBlank weight="bold" /> {date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
              <span className="flex items-center gap-2"><Clock weight="bold" /> {date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span>
              <span className="flex items-center gap-2"><MapPin weight="bold" /> {event.venue}, {event.city}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <span className="label-eyebrow">About</span>
          <h2 className="font-display text-3xl mt-2 mb-6">The experience.</h2>
          <p className="text-base leading-relaxed text-neutral-700">{event.description}</p>

          <div className="mt-12 border-t border-neutral-200 pt-8">
            <span className="label-eyebrow">Venue</span>
            <h3 className="font-display text-2xl mt-2">{event.venue}</h3>
            <p className="text-neutral-600 mt-2">{event.city}, Bangladesh</p>
            <div className="mt-4 aspect-[16/7] bg-neutral-100 border border-neutral-200 flex items-center justify-center text-neutral-400 text-sm rounded-xl">
              <MapPin size={32} className="mr-2" /> Venue map preview
            </div>
          </div>

          <div className="mt-12 border-t border-neutral-200 pt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="stat-card"><ShieldCheck size={24} className="text-[#4F46E5]" /><p className="text-xs uppercase tracking-widest font-bold mt-2">Verified</p></div>
            <div className="stat-card"><div className="font-display text-2xl">{event.sold_count || 0}</div><p className="text-xs uppercase tracking-widest text-neutral-500">Sold</p></div>
            <div className="stat-card"><div className="font-display text-2xl">{event.ticket_types.reduce((a, t) => a + t.total - t.sold, 0)}</div><p className="text-xs uppercase tracking-widest text-neutral-500">Available</p></div>
            <div className="stat-card"><div className="font-display text-2xl">৳{event.min_price?.toLocaleString()}</div><p className="text-xs uppercase tracking-widest text-neutral-500">From</p></div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="border border-black bg-white p-6 sticky top-24 rounded-2xl shadow-xl" data-testid="ticket-selector">
            <span className="label-eyebrow">Tickets</span>
            <h3 className="font-display text-3xl mt-1">Pick your seat.</h3>

            <div className="mt-6 divide-y divide-neutral-200">
              {event.ticket_types.map((t) => {
                const remaining = t.total - t.sold;
                const soldOut = remaining <= 0;
                return (
                  <div key={t.id} className="py-4 flex items-center justify-between gap-3" data-testid={`ticket-row-${t.id}`}>
                    <div className="flex-1">
                      <p className="font-bold">{t.name}</p>
                      {t.description && <p className="text-xs text-neutral-500">{t.description}</p>}
                      <p className="text-xs text-neutral-400 mt-1">{soldOut ? "Sold out" : `${remaining} left`}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl">৳{t.price.toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setQty({ ...qty, [t.id]: Math.max(0, (qty[t.id] || 0) - 1) })}
                        disabled={!qty[t.id]}
                        className="w-8 h-8 border border-neutral-300 hover:border-black disabled:opacity-30 flex items-center justify-center rounded-lg"
                        data-testid={`qty-minus-${t.id}`}
                      ><Minus size={14} /></button>
                      <span className="w-6 text-center font-bold" data-testid={`qty-${t.id}`}>{qty[t.id] || 0}</span>
                      <button
                        onClick={() => setQty({ ...qty, [t.id]: Math.min(remaining, (qty[t.id] || 0) + 1) })}
                        disabled={soldOut || qty[t.id] >= remaining || qty[t.id] >= 10}
                        className="w-8 h-8 border border-neutral-300 hover:border-black disabled:opacity-30 flex items-center justify-center rounded-lg"
                        data-testid={`qty-plus-${t.id}`}
                      ><Plus size={14} /></button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t border-neutral-300 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500">Subtotal ({totalCount})</p>
                <p className="font-display text-3xl" data-testid="event-subtotal">৳{total.toLocaleString()}</p>
              </div>
              <button onClick={onCheckout} className="btn-red" data-testid="checkout-btn">
                Checkout →
              </button>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
